import { FormMessage } from '@gov-design-system-ce/react-form';
import { useTranslationWithNamespace } from '@gov-nx/core/service';
import { useDataBoxMessageFormContext } from '@gov-nx/module/data-box';
import { PageCode } from '@gov-nx/module/page';
import {
	Button,
	Dialog,
	FormCheckbox,
	FormInput,
	FormWidthElementLayout,
	Icon,
} from '@gov-nx/ui/web';

type Props = {
	isEdit: boolean;
};

export const AdditionalDataModal = ({ isEdit }: Props) => {
	const {
		showAdditionalDataModal,
		onAdditionalDataModalClose,
		onAdditionalDataModalConfirm,
		localData,
	} = useDataBoxMessageFormContext();
	const { getLocalizeCurried } = useTranslationWithNamespace();
	const tn = getLocalizeCurried(PageCode['datove-schranky']);

	const hasAdditionalInfo = Boolean(
		localData.temporary?.additionalData?.intoTheirOwnHands ||
		localData.temporary?.additionalData?.senderIdentification ||
		localData.temporary?.additionalData?.toTheHandsOf ||
		localData.temporary?.additionalData?.naseSpisovaZnacka ||
		localData.temporary?.additionalData?.naseCisloJednaci ||
		localData.temporary?.additionalData?.vaseSpisovaZnacka ||
		localData.temporary?.additionalData?.vaseCisloJednaci
	);
	const dataChanged = Boolean(
		localData.temporary?.additionalData?.intoTheirOwnHands !==
			localData.intoTheirOwnHands ||
		localData.temporary?.additionalData?.senderIdentification !==
			localData.senderIdentification ||
		localData.temporary?.additionalData?.toTheHandsOf !==
			localData.toTheHandsOf ||
		localData.temporary?.additionalData?.naseSpisovaZnacka !==
			localData.naseSpisovaZnacka ||
		localData.temporary?.additionalData?.naseCisloJednaci !==
			localData.naseCisloJednaci ||
		localData.temporary?.additionalData?.vaseSpisovaZnacka !==
			localData.vaseSpisovaZnacka ||
		localData.temporary?.additionalData?.vaseCisloJednaci !==
			localData.vaseCisloJednaci
	);
	const buttonDisabled = isEdit ? !dataChanged : !hasAdditionalInfo;

	return (
		<Dialog
			isOpen={showAdditionalDataModal}
			onClose={onAdditionalDataModalClose}
			icon={
				<Icon
					name="info-circle"
					type="components"
					color="primary"
				/>
			}
			title={<h3>{tn('nova-zprava.doplnkove-udaje.nadpis')}</h3>}
			footer={
				<>
					<Button
						type="outlined"
						nativeType="button"
						color="primary"
						size="l"
						onClick={onAdditionalDataModalClose}
						aria-label={
							isEdit
								? tn('nova-zprava.doplnkove-udaje.zrusit-zmeny')
								: tn('nova-zprava.doplnkove-udaje.zrusit')
						}>
						{isEdit
							? tn('nova-zprava.doplnkove-udaje.zrusit-zmeny')
							: tn('nova-zprava.doplnkove-udaje.zrusit')}
					</Button>
					<Button
						nativeType="button"
						color="primary"
						type="solid"
						size="l"
						isDisabled={buttonDisabled}
						onClick={onAdditionalDataModalConfirm}
						aria-label={
							isEdit
								? tn('nova-zprava.doplnkove-udaje.upravit-udaje')
								: tn('nova-zprava.doplnkove-udaje.pridat-udaje')
						}>
						{isEdit
							? tn('nova-zprava.doplnkove-udaje.upravit-udaje')
							: tn('nova-zprava.doplnkove-udaje.pridat-udaje')}
					</Button>
				</>
			}>
			<div className="flex flex-col gap-10">
				<section className="flex flex-col gap-(--templates-margin-s)">
					<h3 className="text-body-l-bold!">
						{tn('nova-zprava.doplnkove-udaje.odesilatel-a-prijemce')}
					</h3>
					<FormWidthElementLayout>
						<FormInput
							field={{
								name: 'temporary.additionalData.toTheHandsOf',
								size: 'm',
							}}
							label={{
								children: tn('nova-zprava.doplnkove-udaje.k-rukam-osoby'),
							}}
						/>
						<FormMessage
							messages={[
								{
									color: 'neutral',
									children: tn(
										'nova-zprava.doplnkove-udaje.k-rukam-osoby-vysvetleni'
									),
								},
							]}
						/>
					</FormWidthElementLayout>
					<div className="flex flex-col">
						<FormCheckbox
							field={{
								name: 'temporary.additionalData.intoTheirOwnHands',
								label: {
									children: tn(
										'nova-zprava.doplnkove-udaje.predat-do-vlastnich-rukou'
									),
								},
							}}
						/>
						<div className="ml-8!">
							<FormMessage
								messages={[
									{
										color: 'neutral',
										children: tn(
											'nova-zprava.doplnkove-udaje.do-vlastnich-rukou-vysvetleni'
										),
									},
								]}
							/>
						</div>
					</div>
				</section>
				<section className="flex flex-col gap-(--templates-margin-s)">
					<div>
						<h3 className="text-body-l-bold!">
							{tn('nova-zprava.doplnkove-udaje.cisla-jednaci-a-spisove-znacky')}
						</h3>
						<p className="text-body-s">
							{tn('nova-zprava.doplnkove-udaje.pokud-znate-vyplnte')}
						</p>
					</div>
					<div className="flex flex-col gap-3">
						<h4 className="text-body-m-bold! text-(--text-secondary)">
							{tn('nova-zprava.doplnkove-udaje.nase')}
						</h4>
						<div className="flex flex-col gap-6 [ md:flex-row ]">
							<FormInput
								field={{
									name: 'temporary.additionalData.naseCisloJednaci',
									size: 'm',
								}}
								label={{
									children: tn(
										'nova-zprava.doplnkove-udaje.nase-cislo-jednaci'
									),
								}}
							/>
							<FormInput
								field={{
									name: 'temporary.additionalData.naseSpisovaZnacka',
									size: 'm',
								}}
								label={{
									children: tn(
										'nova-zprava.doplnkove-udaje.nase-spisova-znacka'
									),
								}}
							/>
						</div>
					</div>
					<div className="flex flex-col gap-3">
						<h4 className="text-body-m-bold! text-(--text-secondary)">
							{tn('nova-zprava.doplnkove-udaje.vase')}
						</h4>
						<div className="flex flex-col gap-6 [ md:flex-row ]">
							<FormInput
								field={{
									name: 'temporary.additionalData.vaseCisloJednaci',
									size: 'm',
								}}
								label={{
									children: tn(
										'nova-zprava.doplnkove-udaje.vase-cislo-jednaci'
									),
								}}
							/>
							<FormInput
								field={{
									name: 'temporary.additionalData.vaseSpisovaZnacka',
									size: 'm',
								}}
								label={{
									children: tn(
										'nova-zprava.doplnkove-udaje.vase-spisova-znacka'
									),
								}}
							/>
						</div>
					</div>
				</section>
			</div>
		</Dialog>
	);
};
